import type { AppError } from '@aibi/shared';
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Field,
  Input,
  PasswordInput,
  Spinner,
} from '@aibi/ui';
import { Bot, Cloud, Download, PlugZap, Workflow } from 'lucide-react';
import { useEffect, useState, type SyntheticEvent, type ReactNode } from 'react';
import { fieldErrorsOf } from '../../api/http-client';
import type {
  AiSettings as AiSettingsData,
  AiTestResult,
  AzureSettingsUpdate,
} from '../services/ai-settings-api';
import { useAdminServices } from '../state/admin-session';
import { azureSecretRequired, emptyAzureForm } from './azure-ai-form';
import { AzureAiFields } from './AzureAiFields';
import { ErrorNotice, Notice } from './ErrorNotice';
import { azureEndpointHost, azureErrors, webhookUrlError, type AzureForm } from './form-rules';

type Provider = AiSettingsData['provider'];

interface StateLabel {
  readonly label: string;
  readonly variant: 'outline' | 'success' | 'warning' | 'destructive';
}

const LOCAL_STATE: StateLabel = { label: 'Interprete local', variant: 'outline' };
const STATE_LABEL: Readonly<Record<string, StateLabel>> = {
  unconfigured: LOCAL_STATE,
  configured: { label: 'Configurado, sin probar', variant: 'warning' },
  ok: { label: 'Conectado', variant: 'success' },
  failing: { label: 'Con errores', variant: 'destructive' },
};

/**
 * Asistente de IA: interprete local (sin servicios externos), un workflow propio de n8n
 * o un modelo desplegado en Azure AI Foundry. URLs, tokens y claves se envian una vez al
 * servidor, que los guarda cifrados; nunca vuelven al navegador.
 */
export function AiSettings() {
  const { aiSettings } = useAdminServices();
  const [settings, setSettings] = useState<AiSettingsData | null>(null);
  const [loadError, setLoadError] = useState<AppError | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void aiSettings.get().then((result) => {
      if (cancelled) return;
      if (result.ok) setSettings(result.value);
      else setLoadError(result.error);
    });
    return () => {
      cancelled = true;
    };
  }, [aiSettings, attempt]);

  if (loadError) {
    return (
      <Page>
        <ErrorNotice error={loadError} />
        <Button
          size="sm"
          variant="outline"
          className="self-start"
          onClick={() => {
            setLoadError(null);
            setAttempt((value) => value + 1);
          }}
        >
          Reintentar
        </Button>
      </Page>
    );
  }
  if (settings === null) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner label="Cargando la configuracion del asistente" />
      </div>
    );
  }
  return (
    <Page>
      <ProviderCard settings={settings} onSaved={setSettings} />
      {settings.provider === 'azure-ai' ? (
        <AzureGuide />
      ) : (
        <WorkflowGuide downloadUrl={aiSettings.workflowExampleUrl} />
      )}
    </Page>
  );
}

function Page({ children }: { readonly children: ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <div>
        <h2 className="text-lg font-semibold">Asistente de IA</h2>
        <p className="text-sm text-muted-foreground">
          Elija como interpreta el asistente las preguntas. Ninguna propuesta se aplica sin su
          confirmacion en el analisis.
        </p>
      </div>
      {children}
    </div>
  );
}

function ProviderCard({
  settings,
  onSaved,
}: {
  readonly settings: AiSettingsData;
  readonly onSaved: (settings: AiSettingsData) => void;
}) {
  const { aiSettings } = useAdminServices();
  const readOnly = settings.source === 'env';
  const [provider, setProvider] = useState<Provider>(settings.provider);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [token, setToken] = useState('');
  const [replaceUrl, setReplaceUrl] = useState(!settings.webhookConfigured);
  const [replaceToken, setReplaceToken] = useState(!settings.tokenConfigured);
  const [clearToken, setClearToken] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<AppError | null>(null);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<AiTestResult | null>(null);
  const [testError, setTestError] = useState<AppError | null>(null);
  const [azure, setAzure] = useState<AzureForm>(() => emptyAzureForm(settings.azure));
  const [replaceEndpoint, setReplaceEndpoint] = useState(!settings.azure?.endpointConfigured);

  const serverErrors = saveError ? fieldErrorsOf(saveError) : {};
  const urlError =
    provider === 'n8n' && replaceUrl
      ? webhookUrl.trim() === ''
        ? 'Pegue la URL de produccion del webhook de n8n.'
        : webhookUrlError(webhookUrl)
      : null;
  const azureHost = replaceEndpoint
    ? azureEndpointHost(azure.endpoint)
    : (settings.azure?.host ?? null);
  const azureSecretNeeded = azureSecretRequired(azure, settings.azure, azureHost);
  const azureFieldErrors =
    provider === 'azure-ai'
      ? azureErrors(azure, {
          replaceEndpoint,
          host: azureHost,
          secretRequired: azureSecretNeeded,
        })
      : {};
  const azureVisibleErrors: Partial<Record<string, string>> = {
    ...Object.fromEntries(
      Object.entries(serverErrors)
        .filter(([path]) => path.startsWith('azure.'))
        .map(([path, message]) => [path.slice('azure.'.length), message]),
    ),
    ...(touched ? azureFieldErrors : {}),
  };
  const state = STATE_LABEL[settings.status.state] ?? LOCAL_STATE;

  const azureUpdate = (): AzureSettingsUpdate => {
    const sp = azure.authType === 'entra-sp';
    return {
      ...(replaceEndpoint ? { endpoint: azure.endpoint.trim() } : {}),
      model: azure.model.trim(),
      api: azure.api,
      authType: azure.authType,
      ...(sp ? { tenantId: azure.tenantId.trim(), clientId: azure.clientId.trim() } : {}),
      ...(azure.secret === '' ? {} : { secret: azure.secret }),
    };
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setTouched(true);
    if (readOnly || busy || urlError || Object.keys(azureFieldErrors).length > 0) return;
    setBusy(true);
    setSaveError(null);
    setSaved(false);
    setTest(null);
    const result = await aiSettings.save({
      provider,
      ...(provider === 'n8n' && replaceUrl ? { webhookUrl: webhookUrl.trim() } : {}),
      ...(provider === 'n8n' && replaceToken && token !== '' ? { authToken: token } : {}),
      ...(clearToken ? { clearToken: true } : {}),
      ...(provider === 'azure-ai' ? { azure: azureUpdate() } : {}),
    });
    setBusy(false);
    setToken('');
    setAzure((current) => ({ ...current, secret: '' }));
    if (!result.ok) {
      setSaveError(result.error);
      return;
    }
    setWebhookUrl('');
    setReplaceUrl(!result.value.webhookConfigured);
    setReplaceToken(!result.value.tokenConfigured);
    setAzure(emptyAzureForm(result.value.azure));
    setReplaceEndpoint(!result.value.azure?.endpointConfigured);
    setClearToken(false);
    setTouched(false);
    setSaved(true);
    onSaved(result.value);
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setTestError(null);
    const result = await aiSettings.test();
    setTesting(false);
    if (result.ok) {
      setTest(result.value);
      onSaved({ ...settings, status: result.value.status });
    } else {
      setTestError(result.error);
    }
  };

  return (
    <Card className="gap-4 p-5">
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-base">Proveedor</CardTitle>
          <CardDescription>
            {readOnly
              ? 'Gestionado por el servidor: la configuracion de instalacion define el proveedor y no se puede cambiar desde aqui.'
              : 'El navegador nunca llama al proveedor: el servidor hace de intermediario y valida cada respuesta.'}
          </CardDescription>
        </div>
        <Badge variant={state.variant}>{state.label}</Badge>
      </CardHeader>

      <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
        <fieldset className="grid gap-2 sm:grid-cols-3" disabled={readOnly}>
          <legend className="sr-only">Proveedor del asistente</legend>
          <ProviderOption
            value="unconfigured"
            current={provider}
            icon={<Bot />}
            title="Interprete local"
            description="Reglas integradas en la app. No envia datos a ningun servicio."
            onSelect={setProvider}
          />
          <ProviderOption
            value="n8n"
            current={provider}
            icon={<Workflow />}
            title="Workflow de n8n"
            description="Su propia instancia de n8n con el modelo de lenguaje que elija."
            onSelect={setProvider}
          />
          <ProviderOption
            value="azure-ai"
            current={provider}
            icon={<Cloud />}
            title="Azure AI Foundry"
            description="Un modelo desplegado en su suscripcion: Llama, Mistral, DeepSeek, Phi..."
            onSelect={setProvider}
          />
        </fieldset>

        {provider === 'azure-ai' && (
          <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
            <AzureAiFields
              form={azure}
              saved={settings.azure}
              readOnly={readOnly}
              replaceEndpoint={replaceEndpoint}
              secretRequired={azureSecretNeeded}
              errors={azureVisibleErrors}
              onChange={setAzure}
              onReplaceEndpoint={() => {
                setReplaceEndpoint(true);
              }}
            />
          </div>
        )}

        {provider === 'n8n' && (
          <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
            {readOnly ? (
              <p className="text-xs text-muted-foreground">
                Webhook en <strong>{settings.host ?? 'host desconocido'}</strong>
                {settings.tokenConfigured ? ' con token configurado.' : ' sin token.'}
              </p>
            ) : (
              <>
                {settings.webhookConfigured && !replaceUrl ? (
                  <SavedSecret
                    label="URL del webhook"
                    value={`Guardada (${settings.host ?? 'host oculto'})`}
                    onReplace={() => {
                      setReplaceUrl(true);
                    }}
                  />
                ) : (
                  <Field
                    label="URL del webhook de n8n"
                    hint="Use la URL de produccion del nodo Webhook (el workflow debe estar activo)."
                    error={(touched ? urlError : null) ?? serverErrors.webhookUrl}
                  >
                    {(control) => (
                      <Input
                        {...control}
                        value={webhookUrl}
                        inputMode="url"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="https://n8n.empresa.com/webhook/aibi"
                        onChange={(event) => {
                          setWebhookUrl(event.target.value);
                        }}
                      />
                    )}
                  </Field>
                )}
                {settings.tokenConfigured && !replaceToken && !clearToken ? (
                  <SavedSecret
                    label="Token de acceso"
                    value="Token guardado"
                    onReplace={() => {
                      setReplaceToken(true);
                    }}
                    onClear={() => {
                      setClearToken(true);
                    }}
                  />
                ) : clearToken ? (
                  <Notice tone="warning">
                    El token se quitara al guardar.{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => {
                        setClearToken(false);
                      }}
                    >
                      Deshacer
                    </button>
                  </Notice>
                ) : (
                  <Field
                    label="Token de acceso (opcional, recomendado)"
                    hint="El mismo valor que la credencial Header Auth del webhook en n8n. Se guarda cifrado en el servidor."
                  >
                    {(control) => (
                      <PasswordInput
                        {...control}
                        value={token}
                        onChange={(event) => {
                          setToken(event.target.value);
                        }}
                      />
                    )}
                  </Field>
                )}
              </>
            )}
          </div>
        )}

        {saveError && <ErrorNotice error={saveError} />}
        {saved && (
          <Notice tone="info">Configuracion guardada. Pruebe la conexion para confirmarla.</Notice>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {!readOnly && (
            <Button type="submit" size="sm" disabled={busy}>
              {busy ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
          {settings.provider !== 'unconfigured' && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={testing || busy}
              onClick={() => void runTest()}
            >
              <PlugZap /> {testing ? 'Probando...' : 'Probar conexion'}
            </Button>
          )}
        </div>
        {test && (
          <Notice tone={test.ok ? 'info' : 'error'}>
            <p>{test.message}</p>
            {test.hint && <p className="mt-1 text-foreground/80">{test.hint}</p>}
          </Notice>
        )}
        {testError && <ErrorNotice error={testError} />}
      </form>
    </Card>
  );
}

function ProviderOption({
  value,
  current,
  icon,
  title,
  description,
  onSelect,
}: {
  readonly value: Provider;
  readonly current: Provider;
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
  readonly onSelect: (value: Provider) => void;
}) {
  const selected = value === current;
  return (
    <label
      className={cn(
        'flex cursor-pointer gap-3 rounded-lg border p-3 text-sm transition-colors has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-70',
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50',
      )}
    >
      <input
        type="radio"
        name="ai-provider"
        className="sr-only"
        checked={selected}
        onChange={() => {
          onSelect(value);
        }}
      />
      <span className="mt-0.5 text-primary [&_svg]:size-4">{icon}</span>
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </label>
  );
}

function SavedSecret({
  label,
  value,
  onReplace,
  onClear,
}: {
  readonly label: string;
  readonly value: string;
  readonly onReplace: () => void;
  readonly onClear?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium">{label}</span>
        <Badge variant="success" className="self-start">
          {value}
        </Badge>
      </div>
      <div className="flex gap-1">
        <Button type="button" size="sm" variant="ghost" onClick={onReplace}>
          Reemplazar
        </Button>
        {onClear && (
          <Button type="button" size="sm" variant="ghost" onClick={onClear}>
            Quitar
          </Button>
        )}
      </div>
    </div>
  );
}

function AzureGuide() {
  return (
    <Card className="gap-3 p-5">
      <CardHeader>
        <CardTitle className="text-base">Como conectar un modelo de Microsoft Foundry</CardTitle>
        <CardDescription>
          Sirven los modelos de chat desplegados en su recurso: GPT, Claude, DeepSeek, Llama,
          Mistral, Phi o Grok. Los de mejor seguimiento de instrucciones dan respuestas validas con
          mas frecuencia.
        </CardDescription>
      </CardHeader>
      <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
        <li>
          En Foundry, abra Ver implementaciones y anote el nombre exacto de la implementacion (no el
          del modelo del catalogo).
        </li>
        <li>
          Copie el punto de conexion del proyecto o el de Azure OpenAI de la pagina de inicio (por
          ejemplo{' '}
          <code className="rounded bg-muted px-1">https://mi-recurso.services.ai.azure.com</code>).
        </li>
        <li>
          Con Claude, elija la API &quot;Claude (API Messages de Anthropic)&quot;; se selecciona
          sola si el nombre de la implementacion empieza por &quot;claude&quot;.
        </li>
        <li>
          Elija la autenticacion: la clave de API de la pagina de inicio o un service principal de
          Entra ID con el rol &quot;Cognitive Services User&quot; sobre el recurso.
        </li>
        <li>Guarde y pulse Probar conexion.</li>
      </ol>
      <p className="text-xs text-muted-foreground">
        El servidor envia solo metadatos del modelo semantico (nunca filas de datos ni SQL) y valida
        cada respuesta antes de proponerla. Ninguna propuesta se aplica sin su confirmacion.
      </p>
    </Card>
  );
}

function WorkflowGuide({ downloadUrl }: { readonly downloadUrl: string }) {
  return (
    <Card className="gap-3 p-5">
      <CardHeader>
        <CardTitle className="text-base">Como preparar el workflow de n8n</CardTitle>
        <CardDescription>
          Solo es necesario si elige n8n. El interprete local funciona sin configuracion.
        </CardDescription>
      </CardHeader>
      <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
        <li>
          Descargue el workflow de ejemplo e importelo en n8n (menu Workflows, Import from file).
        </li>
        <li>
          En el nodo Webhook, cree una credencial <strong>Header Auth</strong> con el nombre{' '}
          <code className="rounded bg-muted px-1">Authorization</code> y el valor{' '}
          <code className="rounded bg-muted px-1">Bearer su-token</code>.
        </li>
        <li>
          Configure el nodo del modelo de lenguaje con su propia credencial (OpenAI, Azure, etc.).
        </li>
        <li>Active el workflow y copie la URL de produccion del webhook.</li>
        <li>
          Pegue aqui la URL y el token (solo el valor, sin &quot;Bearer&quot;), guarde y pulse
          Probar conexion.
        </li>
      </ol>
      <p className="text-xs text-muted-foreground">
        El contrato completo (entrada, salida y limites) esta en{' '}
        <code className="rounded bg-muted px-1">docs/n8n/ai-workflow-spec.md</code>. La salida del
        workflow se valida siempre en el servidor y en el navegador.
      </p>
      <a
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'self-start')}
        href={downloadUrl}
        download="aibi-assistant.workflow.example.json"
      >
        <Download /> Descargar workflow de ejemplo
      </a>
    </Card>
  );
}
